/** @jsxImportSource theme-ui */
import React, { useContext, useMemo } from 'react';
import { Grid } from 'semantic-ui-react';

import { AppStateContext } from 'api/AppStateContext';
import * as dto from 'client/dto';
import { AggregationCat } from 'models/DiseaseModels';
import { parseDateAsLocal, parseIsoDateStartOfDayUtc0 } from 'utils/dateTimeHelpers';
import {
  getRiskLikelihood,
  getRiskMagnitude,
  MapAggregationCat2AggregationDays,
  MapAggregationCat2TooltipLabel,
} from 'utils/modelHelpers';
import { formatNumber } from 'utils/stringFormattingHelpers';

import { Typography } from 'components/_common/Typography';
import { BdTooltip } from 'components/_controls/BdTooltip';
import { SparkTooltip } from 'components/_controls/DiseaseActivityControls/DaSparkTooltip';
import { SparklineDatumType } from 'components/_graphs/_common/DiseaseActivityModels';
import { MakeMockGraphData_Spark } from 'components/_graphs/_common/mockSampleLineData';
import { BdSparkGraphBars } from 'components/_graphs/BdSparkGraph/BdSparkGraphBars';

type EventMetaDataCardProps = {
  isLocal: boolean;
  caseCounts: dto.CaseCountModel;
  importationRisk: dto.RiskModel;
  exportationRisk: dto.RiskModel;
  sparkData: dto.TemporalCaseCountModel[];
  aggregationCat: AggregationCat;
  showDividingLines?: boolean;
};

//= ====================================================================================================================================
const UseFakeSparkData = false;
//= ====================================================================================================================================

const EventMetaDataCard: React.FC<EventMetaDataCardProps> = ({
  isLocal,
  caseCounts,
  importationRisk,
  exportationRisk,
  sparkData,
  aggregationCat,
  showDividingLines = false,
}) => {
  const { reportedCases, deaths } = { ...caseCounts };
  const { appState } = useContext(AppStateContext);

  const timeframeStartDate = useMemo(
    () => parseDateAsLocal(appState.startDate),
    [appState.startDate]
  );

  const risk = importationRisk || exportationRisk;
  const travellersText = getRiskMagnitude(risk);
  const likelihoodText = getRiskLikelihood(risk);

  const graphData = useMemo(
    () =>
      UseFakeSparkData
        ? MakeMockGraphData_Spark()
        : (sparkData || []).map(
            x =>
              ({
                date: parseIsoDateStartOfDayUtc0(x.startDate),
                cases: x.caseCounts?.reportedCases || 0,
              } as SparklineDatumType)
          ),
    [sparkData]
  );

  return (
    <Grid columns="equal" relaxed="very" divided={showDividingLines}>
      <Grid.Row>
        <Grid.Column className="override-semantic-column-flex-grow1">
          <BdTooltip
            fixedWidth="175px"
            customPopup={
              <SparkTooltip
                sumCases={caseCounts?.reportedCases}
                timeframeStartDate={timeframeStartDate}
                aggregationDaysStr={MapAggregationCat2TooltipLabel[aggregationCat]}
              />
            }
          >
            <Typography variant="caption" color="deepSea50" marginBottom="6px">
              Case activity
            </Typography>
            <div sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <BdSparkGraphBars
                width={92}
                height={60}
                graphData={graphData}
                minDate={timeframeStartDate}
                aggregationDays={MapAggregationCat2AggregationDays[aggregationCat]}
                isLoading={!sparkData}
                isDateTicksUppercase
                padding={{
                  left: 0,
                  right: 0,
                  top: 1,
                  bottom: 20,
                }}
              />
            </div>
          </BdTooltip>
        </Grid.Column>
        <Grid.Column className="override-semantic-column-flex-grow2">
          <Grid columns={2} relaxed="very" divided={(showDividingLines && 'vertically') || false}>
            <Grid.Row divided={showDividingLines}>
              <Grid.Column>
                <Typography variant="caption" color="deepSea50">
                  Reported cases
                </Typography>
                <Typography variant="subtitle2" color="stone90">
                  {formatNumber(reportedCases, 'case', 'cases', 'emdash-0')}
                </Typography>
              </Grid.Column>
              <Grid.Column>
                <Typography variant="caption" color="deepSea50">
                  Reported deaths
                </Typography>
                <Typography variant="subtitle2" color="stone90">
                  {formatNumber(deaths, 'death', 'deaths', 'emdash-0')}
                </Typography>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row divided={showDividingLines}>
              <Grid.Column>
                <>
                  <Typography variant="caption" color="deepSea50">
                    Likelihood of case {importationRisk ? 'importation' : 'exportation'}/month
                  </Typography>
                  <Typography variant="subtitle2" color="stone90">
                    {likelihoodText}
                  </Typography>
                </>
              </Grid.Column>
              <Grid.Column>
                <>
                  <Typography variant="caption" color="deepSea50">
                    Estimated case {importationRisk ? 'importations' : 'exportations'}/month
                  </Typography>
                  <Typography variant="subtitle2" color="stone90">
                    {travellersText}
                  </Typography>
                </>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default EventMetaDataCard;
